import type {
  ClientBusinessContract,
  ClientSlug,
  ClientThemeContract,
  ThemePresetName,
} from "./types";

import { resolveClientSlug as resolveClientSlugPure } from "./resolveClient";

/**
 * V1: file-based contracts.
 * Later you can swap this loader to fetch remote JSON, but the call sites don't change.
 *
 * IMPORTANT:
 * - Imports are explicit (no dynamic paths).
 * - Business is client-owned.
 * - Theme is BOILERPLATE-owned (preset-based).
 */

// -----------------------------
// Clients (business contracts)
// -----------------------------
import demoBusiness from "./clients/demo/business.json";
import marrielBusiness from "./clients/marriel/business.json";

// -----------------------------
// Theme presets (boilerplate-owned)
// -----------------------------
import candyPink from "./themes/candy-pink.json";
import clinicGreen from "./themes/clinic-green.json";
import forestEmerald from "./themes/forest-emerald.json";
import marrielBrazilUrban from "./themes/marriel-brazil-urban.json";
import petroleumTeal from "./themes/petroleum-teal.json";
import premiumGold from "./themes/premium-gold.json";

// -----------------------------
// Wiring
// -----------------------------
const DEFAULT_CLIENT: ClientSlug = "marriel";
const DEFAULT_THEME_PRESET: ThemePresetName = "marriel-brazil-urban";

const BUSINESS_BY_CLIENT: Record<ClientSlug, ClientBusinessContract> = {
  demo: demoBusiness as ClientBusinessContract,
  marriel: marrielBusiness as ClientBusinessContract,
};

const THEME_PRESET_BY_KEY: Record<ThemePresetName, ClientThemeContract> = {
  "marriel-brazil-urban": marrielBrazilUrban,
  "premium-gold": premiumGold,
  "petroleum-teal": petroleumTeal,
  "forest-emerald": forestEmerald,
  "clinic-green": clinicGreen,
  "candy-pink": candyPink,
};

function isThemePresetName(v: unknown): v is ThemePresetName {
  return typeof v === "string" && v in THEME_PRESET_BY_KEY;
}

/**
 * Resolve theme preset for a business contract.
 * Rule:
 * - VITE_THEME_PRESET can force a known preset (for testing)
 * - business.themePreset selects a known preset
 * - fail-closed => DEFAULT_THEME_PRESET
 */
function resolveThemePreset(business: ClientBusinessContract): ThemePresetName {
  const rawPreset = import.meta.env.VITE_THEME_PRESET;

  // Guard: never call .trim() on non-string (prevents Vercel runtime crash)
  const forced =
    typeof rawPreset === "string" && rawPreset.trim() ? rawPreset.trim() : "";

  if (forced && isThemePresetName(forced)) {
    return forced;
  }

  const preset = business.themePreset;
  if (preset && isThemePresetName(preset)) {
    return preset;
  }

  return DEFAULT_THEME_PRESET;
}

export function getClientBusiness(client: ClientSlug): ClientBusinessContract {
  const found = BUSINESS_BY_CLIENT[client];
  if (!found) {
    throw new Error(`Unknown client (business): ${client}`);
  }
  return found;
}

/**
 * Theme is derived from a preset (not per-client JSON).
 * Enforces: client chooses from existing themes only.
 */
export function getClientTheme(client: ClientSlug): ClientThemeContract {
  const business = getClientBusiness(client);
  const presetKey = resolveThemePreset(business);
  return THEME_PRESET_BY_KEY[presetKey];
}

/**
 * Active helpers: resolve the client slug via env and return contracts.
 */
export function getActiveBusiness(): ClientBusinessContract {
  return getClientBusiness(getActiveClientSlug());
}

export function getActiveTheme(): ClientThemeContract {
  return getClientTheme(getActiveClientSlug());
}

/**
 * Optional helpers (useful for docs/admin later).
 */
export function listClients(): ClientSlug[] {
  return Object.keys(BUSINESS_BY_CLIENT) as ClientSlug[];
}

export function getActiveClientSlug(): ClientSlug {
  const raw = resolveClientSlugPure(listClients(), DEFAULT_CLIENT);
  return typeof raw === "string" ? (raw as ClientSlug) : DEFAULT_CLIENT;
}

export function listThemePresets(): ThemePresetName[] {
  return Object.keys(THEME_PRESET_BY_KEY) as ThemePresetName[];
}
